import {
    ReactElement,
    useState,
} from 'react';

import {
    Center,
    Checkbox,
    SimpleGrid,
    Table,
    Text,
} from '@mantine/core';

import {useOffers} from '../hooks/useOffers.ts';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    SimpleTablePagination,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';

import PropTypes from 'prop-types';
import {IOfferTableColumn, Offer} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {formatTimestamp} from '../../../utils/TableTimestamps';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/OffersService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 extraTools,
                                 settings,
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[],
    selectedRecords: Offer[],
    setSelectedRecords: (records: Offer[]) => void,
}) => {

    const {allowedActions} = useVerifiedPermissions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Offer | null>(null);

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        fetchRecords,
        handleFilterByKeyword,
    } = useOffers();

    const handleRecordClick = (record: Offer) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: Offer) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as Offer);
            queryClient.invalidateQueries({queryKey: ['offers']});
        }
    };

    const handleRowSelection = (row: { id: string }) => {
        const exists = selectedRecords.find(r => r.id === row.id);
        if (exists) {
            setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
        } else {
            const fullRecord = visibleItems.find(r => r.id === row.id);
            if (fullRecord) {
                setSelectedRecords([...selectedRecords, fullRecord]);
            }
        }
    };

    const handleSelectAllRows = () => {
        if (selectedRecords.length === visibleItems.length) {
            setSelectedRecords([]);
        } else {
            setSelectedRecords([...visibleItems]);
        }
    };

    const getColumns = () => {
        const cols: IOfferTableColumn[] = [];
        if (allowedActions.includes('DeleteOffer')) {
            cols.push({
                id: 'select',
                title: '',
                render: item => {
                    if (item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername) {
                        return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                    } else {
                        return (<Center><Checkbox disabled={true}></Checkbox></Center>);
                    }
                },
            });
        }

        cols.push({
            title: I18n.get('Name'),
            width: '1fr',
            render: (item: Offer) => (
                <Text
                    c="blue"
                    style={{cursor: 'pointer'}}
                    onClick={() => handleRecordClick(item)}
                >
                    {item.name}
                </Text>
            ),
        });

        cols.push({
            title: I18n.get('URL'),
            width: '1fr',
            render: (item: Offer) => item.url,
        });

        cols.push({
            title: I18n.get('Phone'),
            width: '1fr',
            render: (item: Offer) => item.phone,
        });

        cols.push({
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Offer) => formatTimestamp(item.created as string),
        });

        return cols;
    };

    const selection: Record<string, Offer> = {};
    selectedRecords.forEach(r => {
        selection[r.id] = r;
    });

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={10}
                handleItemsPerPageChange={() => {}}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumns() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumns() as TableBodyColumn[]}
                />
            </Table>

            <SimpleTablePagination
                totalPages={1}
                visibleCount={visibleItems.length}
                handlePageChange={() => fetchRecords()}
            />

            <FormDrawer
                record={selectedRecord || {} as Offer}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};

RecordsTable.propTypes = {
    extraTools: PropTypes.array,
    settings: PropTypes.object,
};
